import React, { useEffect, useRef, useState } from "react";
import PopUp from "../../../../components/common/PopUp/PopUp";
import ProjectHelpModal from "../ProjectHelper/ProjectHelpModal";
import ProjectFormExampleModal from "../ProjectHelper/ProjectFormExampleModal";

import DisplayProjectContainer from '../Container/DisplayProjectContainer';
import ManageProjectContainer from '../Container/ManageProjectContainer';
import {
  ProjectFailureBanner,
  ProjectSuccessBanner,
} from '../ProjectHelper/ProjectStatusBanner';
import {
  getGlobalToggleMode,
  getProjectDisplayToggleKey,
  getProjectToggleState,
  resetProjectToggleState,
  setGlobalToggleMode,
  setProjectToggleState,
  ToggleStateMap,
} from './displayToggleStateStore';

/**
 * Renders the project details tab and exposes project add/edit actions when the caller has edit permission.
 * @returns The project details tab content.
 */
export const ProjectDetailsTab: React.FC<any> = ({ project, selectedProject, canEdit = false, ...props }) => {
  const containerRef = useRef<HTMLDivElement | null>(null);
  const manageProjectRef = useRef<HTMLDivElement | null>(null);
  const editProjectButtonRef = useRef<HTMLButtonElement | null>(null);
  const manageActionContainerRef = useRef<HTMLDivElement | null>(null);
  const fallbackSuccessBannerRef = useRef<HTMLDivElement | null>(null);
  const fallbackFailureBannerRef = useRef<HTMLDivElement | null>(null);
  const applyingToggleStateRef = useRef(false);
  const lastKnownDisplayStateRef = useRef<ToggleStateMap | null>(null);
  const [expandedAll, setExpandedAll] = useState(false);
  const [showManageView, setShowManageView] = useState(false);
  const [isEditingProject, setIsEditingProject] = useState(false);
  const [isAddActionActive, setIsAddActionActive] = useState(false);
  const [isEditActionActive, setIsEditActionActive] = useState(false);
  const [fromBannerAdd, setFromBannerAdd] = useState(false);
  const hasTriggeredBannerAddRef = useRef(false);
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const [isFormExampleModalOpen, setIsFormExampleModalOpen] = useState(false);

  // Open help or form-example modals when the popup anchors are clicked
  useEffect(() => {
    const handleHelpClick = (e: MouseEvent) => {
      const target = e.target as HTMLElement;
      if (target.closest('[data-open-modal="project-help"]')) {
        e.preventDefault();
        setIsHelpModalOpen(true);
      }
      if (target.closest('[data-open-modal="project-form-example"]')) {
        e.preventDefault();
        setIsFormExampleModalOpen(true);
      }
    };
    document.addEventListener('click', handleHelpClick);
    return () => document.removeEventListener('click', handleHelpClick);
  }, []);

  const scheduleAfterToggleDomUpdate = React.useCallback((fn: () => void) => {
    if (globalThis.window === undefined || typeof globalThis.window.requestAnimationFrame !== 'function') {
      setTimeout(fn, 0);
      return;
    }

    globalThis.window.requestAnimationFrame(() => {
      globalThis.window.requestAnimationFrame(fn);
    });
  }, []);

  const getDisplayToggles = React.useCallback((): HTMLElement[] => {
    if (!containerRef.current) return [];
    return Array.from(containerRef.current.querySelectorAll('.ds-contenttoggle'));
  }, []);

  const getToggleStateId = React.useCallback((toggle: HTMLElement, index: number): string => {
    const headingText =
      toggle
        .querySelector('.ds-contenttoggle__expandable-heading-text')
        ?.textContent
        ?.trim() ?? '';
    return headingText || `toggle-${index}`;
  }, []);

  const isToggleOpen = React.useCallback((toggle: HTMLElement): boolean => {
    const heading = toggle.querySelector('.ds-contenttoggle__expandable-heading');
    const content = toggle.querySelector('.ds-contenttoggle__content');
    const ariaIndicatesOpen = heading?.getAttribute('aria-expanded') === 'true';
    const panelIndicatesOpen = content?.classList.contains('show');
    return Boolean(ariaIndicatesOpen || panelIndicatesOpen || (content && content.clientHeight > 0));
  }, []);

  const snapshotDisplayToggleState = React.useCallback(() => {
    if (showManageView) return;
    if (applyingToggleStateRef.current) return;

    const toggles = getDisplayToggles();
    if (toggles.length === 0) return;

    const state: ToggleStateMap = {};
    const globalMode = getGlobalToggleMode();
    toggles.forEach((toggle, index) => {
      const stateId = getToggleStateId(toggle, index);
      if (globalMode === 'expand-all') {
        state[stateId] = true;
      } else if (globalMode === 'collapse-all') {
        state[stateId] = false;
      } else {
        state[stateId] = isToggleOpen(toggle);
      }
    });

    const projectKey = getProjectDisplayToggleKey(project, selectedProject);
    setProjectToggleState(projectKey, state);
    lastKnownDisplayStateRef.current = state;

    const allExpanded = getGlobalToggleMode() === 'expand-all' || toggles.every((toggle) => isToggleOpen(toggle));
    setExpandedAll(allExpanded);
  }, [
    showManageView,
    getDisplayToggles,
    scheduleAfterToggleDomUpdate,
    getToggleStateId,
    isToggleOpen,
    project,
    selectedProject,
  ]);

  const getExpectedToggleState = React.useCallback((toggle: HTMLElement, index: number, globalMode: ReturnType<typeof getGlobalToggleMode>, preferredState: ToggleStateMap | null): boolean => {
    if (globalMode === 'expand-all') {
      return true;
    }

    if (globalMode === 'collapse-all') {
      return false;
    }

    if (preferredState) {
      return Boolean(preferredState[getToggleStateId(toggle, index)]);
    }

    // Default: only first accordion open on initial load.
    return index === 0;
  }, [getToggleStateId]);

  const applyStoredDisplayToggleState = React.useCallback((): boolean => {
    if (showManageView) return false;

    const toggles = getDisplayToggles();
    if (toggles.length === 0) return false;

    const projectKey = getProjectDisplayToggleKey(project, selectedProject);
    const savedState = getProjectToggleState(projectKey);
    const globalMode = getGlobalToggleMode();
    const inheritedState = lastKnownDisplayStateRef.current;
    const preferredState = savedState ?? inheritedState;
    const nextState: ToggleStateMap = {};

    applyingToggleStateRef.current = true;
    toggles.forEach((toggle, index) => {
      const heading = toggle.querySelector<HTMLElement>('.ds-contenttoggle__expandable-heading');
      if (!heading) return;
      const isOpen = isToggleOpen(toggle);
      const stateId = getToggleStateId(toggle, index);
      const shouldOpen = getExpectedToggleState(toggle, index, globalMode, preferredState);

      nextState[stateId] = shouldOpen;
      if (shouldOpen !== isOpen) {
        heading.click();
      }
    });

    applyingToggleStateRef.current = false;
    setProjectToggleState(projectKey, nextState);
    lastKnownDisplayStateRef.current = nextState;

    const allExpanded = getGlobalToggleMode() === 'expand-all' || (Object.values(nextState).length > 0 && Object.values(nextState).every(Boolean));
    setExpandedAll(allExpanded);
    return true;
  }, [showManageView, getDisplayToggles, project, selectedProject, isToggleOpen, getToggleStateId, getExpectedToggleState]);

  React.useEffect(() => {
    if (showManageView) return;
    setExpandedAll(getGlobalToggleMode() === 'expand-all');
  }, [selectedProject, project, showManageView]);

  React.useEffect(() => {
    if (showManageView) return;

    let cancelled = false;
    let retryTimer: ReturnType<typeof setTimeout> | null = null;
    let settleTimer: ReturnType<typeof setTimeout> | null = null;
    let restoreCompleted = false;
    let hasAppliedAtLeastOnce = false;
    let retryCount = 0;
    const maxRetries = 8;
    let observer: MutationObserver | null = null;

    const finishRestoreAttempt = () => {
      restoreCompleted = true;
      if (retryTimer) {
        clearTimeout(retryTimer);
        retryTimer = null;
      }
      if (observer) {
        observer.disconnect();
        observer = null;
      }
      if (settleTimer) {
        clearTimeout(settleTimer);
        settleTimer = null;
      }
    };

    const scheduleFinishRestoreAttempt = () => {
      if (settleTimer) {
        clearTimeout(settleTimer);
      }

      // Keep observing briefly after a successful apply because project-switch flows
      // can trigger a follow-up DOM re-init that resets toggle classes.
      settleTimer = setTimeout(() => {
        finishRestoreAttempt();
      }, 250);
    };

    const tryApplyState = () => {
      if (cancelled || showManageView || restoreCompleted) return;

      scheduleAfterToggleDomUpdate(() => {
        if (cancelled || showManageView || restoreCompleted) return;

        const didApply = applyStoredDisplayToggleState();
        if (didApply) {
          hasAppliedAtLeastOnce = true;
          if (retryTimer) {
            clearTimeout(retryTimer);
            retryTimer = null;
          }
          scheduleFinishRestoreAttempt();
          return;
        }

        if (hasAppliedAtLeastOnce) {
          return;
        }

        if (retryCount >= maxRetries) return;
        retryCount += 1;
        retryTimer = setTimeout(tryApplyState, 50);
      });
    };

    tryApplyState();

    const rootElement = containerRef.current;
    if (!rootElement) {
      return () => {
        cancelled = true;
        if (retryTimer) clearTimeout(retryTimer);
      };
    }

    observer = new MutationObserver(() => {
      if (cancelled || showManageView || restoreCompleted) return;
      if (getDisplayToggles().length === 0) return;

      scheduleAfterToggleDomUpdate(() => {
        if (cancelled || showManageView || restoreCompleted) return;
        if (applyStoredDisplayToggleState()) {
          hasAppliedAtLeastOnce = true;
          scheduleFinishRestoreAttempt();
        }
      });
    });

    observer.observe(rootElement, { childList: true, subtree: true });

    return () => {
      cancelled = true;
      if (retryTimer) clearTimeout(retryTimer);
      if (settleTimer) clearTimeout(settleTimer);
      if (observer) observer.disconnect();
    };
  }, [selectedProject, project, showManageView, applyStoredDisplayToggleState, scheduleAfterToggleDomUpdate, getDisplayToggles]);

  React.useEffect(() => {
    if (showManageView) return;
    if (!containerRef.current) return;

    const rootElement = containerRef.current;
    const onToggleInteraction = (event: Event) => {
      const target = event.target as HTMLElement | null;
      if (!target) return;
      const heading = target.closest('.ds-contenttoggle__expandable-heading');
      if (!heading) return;

      scheduleAfterToggleDomUpdate(() => {
        snapshotDisplayToggleState();
      });
    };

    rootElement.addEventListener('click', onToggleInteraction);
    return () => {
      rootElement.removeEventListener('click', onToggleInteraction);
    };
  }, [showManageView, snapshotDisplayToggleState, scheduleAfterToggleDomUpdate]);

  React.useEffect(() => {
    if (!showManageView) return;
    setTimeout(() => {
      const el = manageProjectRef.current;
      if (el && typeof el.scrollIntoView === "function") {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }

      // Keep keyboard focus on the edit trigger while entering edit mode.
      if (isEditingProject) {
        editProjectButtonRef.current?.focus();
        return;
      }

      if (el && typeof el.focus === "function") {
        el.focus();
      }
    }, 0);
  }, [showManageView, isEditingProject]);

  const toggleAll = (expand: boolean) => {
    if (!containerRef.current) return;
    const toggles = Array.from(
      containerRef.current.querySelectorAll<HTMLElement>(".ds-contenttoggle"),
    );
    toggles.forEach((toggle) => {
      const heading = toggle.querySelector(
        ".ds-contenttoggle__expandable-heading",
      ) as HTMLElement;
      const content = toggle.querySelector(
        ".ds-contenttoggle__content",
      ) as HTMLElement;
      if (!heading || !content) return;
      const isOpen =
        content.clientHeight > 0 ||
        toggle.classList.contains("ds-contenttoggle__is-open");
      if (expand && !isOpen) heading.click();
      if (!expand && isOpen) heading.click();
    });

    // Set global mode for collapse
    if (!expand) {
      setGlobalToggleMode('collapse-all');
    }
  };

  const handleExpandClick = () => {
    const currentMode = getGlobalToggleMode();
    const shouldExpand = currentMode !== 'expand-all';
    const newMode = shouldExpand ? 'expand-all' : null;
    
    setGlobalToggleMode(newMode);

    const toggles = getDisplayToggles();
    if (toggles.length > 0) {
      const state: ToggleStateMap = {};
      toggles.forEach((toggle, index) => {
        const stateId = getToggleStateId(toggle, index);
        state[stateId] = shouldExpand;
      });

      const projectKey = getProjectDisplayToggleKey(project, selectedProject);
      setProjectToggleState(projectKey, state);
      lastKnownDisplayStateRef.current = state;
    }

    toggleAll(shouldExpand);
    setExpandedAll(shouldExpand);
    scheduleAfterToggleDomUpdate(() => {
      snapshotDisplayToggleState();
    });
  };

  React.useEffect(() => {
    if (props.triggerAddFromBanner && !hasTriggeredBannerAddRef.current) {
      hasTriggeredBannerAddRef.current = true;
      props.onAddFromBannerConsumed?.();
      setFromBannerAdd(true);
      setIsAddActionActive(true);
      setIsEditActionActive(false);
      setIsEditingProject(false);
      setShowManageView(true);
    }
  // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [props.triggerAddFromBanner]);

  const handleAddNewProject = (e?: React.MouseEvent) => {
    e?.preventDefault();
    snapshotDisplayToggleState();
    setIsAddActionActive(true);
    setIsEditActionActive(false);
    setIsEditingProject(false);
    setShowManageView(true);
  };

  /**
   * Handles Edit Project click. For Safari, ensures Project tab is focused and scrolled smoothly to start of screen.
   */
  const handleEditProject = (e?: React.MouseEvent) => {
    e?.preventDefault();
    snapshotDisplayToggleState();
    setIsAddActionActive(false);
    setIsEditActionActive(true);
    setIsEditingProject(true);
    setShowManageView(true);

    // Safari focus/scroll fix: after state update, focus and scroll tab
    setTimeout(() => {
      const isSafari = /^((?!chrome|android).)*safari/i.test(navigator.userAgent);
      if (isSafari) {
        const tabLinks = Array.from(document.querySelectorAll('.ds-tabs__link'));
        // Project Details tab is always first (id: 1)
        const detailsTab = tabLinks[0] as HTMLElement | undefined;
        if (detailsTab) {
          detailsTab.focus({ preventScroll: true });
          detailsTab.scrollIntoView({ behavior: 'smooth', block: 'start', inline: 'start' });
        }
      }
    }, 250);
  };

  const handleManageProjectSave = async (payload: any) => {
    let result: any = null;
    const isOperationSuccessful = (response: any): boolean => {
      if (response?.success === false) {
        return false;
      }
      if (response?.data?.success === false) {
        return false;
      }
      return true;
    };

    if (isEditingProject && typeof props.onUpdate === 'function') {
      result = await props.onUpdate(payload);
      if (isOperationSuccessful(result)) {
        const projectKey = getProjectDisplayToggleKey(project, selectedProject);
        resetProjectToggleState(projectKey);
        setGlobalToggleMode(null);
        lastKnownDisplayStateRef.current = null;
        setIsEditActionActive(false);
        setIsEditingProject(false);
        setShowManageView(false);
        // Reset unsaved changes after successful update
        if (typeof props.onSetUnsavedChanges === 'function') {
          props.onSetUnsavedChanges(false);
        }
      }
      return result;
    }

    if (typeof props.onSave === 'function') {
      result = await props.onSave(payload);
      if (isOperationSuccessful(result)) {
        const projectKey = getProjectDisplayToggleKey(project, selectedProject);
        resetProjectToggleState(projectKey);
        setGlobalToggleMode(null);
        lastKnownDisplayStateRef.current = null;
        setIsAddActionActive(false);
        setShowManageView(false);
        setFromBannerAdd(false);
        hasTriggeredBannerAddRef.current = false;
        props.onAddFromBannerCompleted?.();
        // Reset unsaved changes after successful save
        if (typeof props.onSetUnsavedChanges === 'function') {
          props.onSetUnsavedChanges(false);
        }
      }
      return result;
    }

    return null;
  };

  return (
    <div className="pt-4" ref={containerRef}>
      <ProjectSuccessBanner
        show={Boolean(props.showSuccessBanner)}
        message={props.successBannerMessage ?? ''}
        bannerRef={props.successBannerRef ?? fallbackSuccessBannerRef}
      />
      <ProjectFailureBanner
        show={Boolean(props.showFailureBanner)}
        message={props.failureBannerMessage ?? ''}
        bannerRef={props.failureBannerRef ?? fallbackFailureBannerRef}
      />
          <div className="flex items-center justify-between mt-10!">
            <div className="flex items-center gap-3">
              <div className={`flex items-center gap-3 ${fromBannerAdd ? 'hidden' : ''}`}>
                 { canEdit && (
                  <>
                    <button
                      className={`button py-0! flex items-center group hover:text-white! focus:text-white! ${isAddActionActive ? 'btn-primary bg-[#003B71]! text-white!' : '-primary -light'}`}
                      data-analytics-type="button"
                      data-analytics-click="Default"
                      onClick={handleAddNewProject}
                    >
                      <img
                        src="/assets/images/plus-outline-white.svg"
                        alt="Plus icon"
                        className={`min-w-6 min-h-6 group-focus:brightness-0! group-focus:invert! group-hover:brightness-0! group-hover:invert! ${isAddActionActive ? 'brightness-0! invert!' : ''}`}
                        style={{ verticalAlign: 'middle', filter: isAddActionActive ? undefined : 'brightness(2)' }}
                      />
                      {' '}
                      Add new project
                    </button>
                    <button
                      ref={editProjectButtonRef}
                      className={`button py-0! flex items-center group hover:text-white! focus:text-white! ${isEditActionActive ? 'btn-primary bg-[#003B71]! text-white!' : '-inverted hover:bg-[#003B71]!'}`}
                      data-analytics-type="button"
                      data-analytics-click="Default"
                      onClick={handleEditProject}
                    >
                      <img
                        src="/assets/images/pencil-outline.svg"
                        alt=""
                        className={`min-w-6 min-h-6 group-focus:brightness-0! group-focus:invert! group-hover:brightness-0! group-hover:invert! ${isEditActionActive ? 'brightness-0! invert!' : ''}`}
                      />{' '}
                      {isEditingProject ? 'Editing this project' : 'Edit this project'}
                    </button>
                  </>
                )}
              </div>
               <div ref={manageActionContainerRef} className="flex items-center gap-3" />
            </div>
            <div className={`flex items-center gap-4 ${fromBannerAdd ? 'hidden' : ''}`}>
              { !isEditingProject && !isAddActionActive && (
                  <button
                    onClick={(e) => {
                      e.preventDefault();
                      handleExpandClick();
                    }}
                    className="h4 text-kp-blue-dark!"
                  >
                    {expandedAll ? "Collapse all" : "Expand all"}
                    <img
                      src="/assets/images/maximize-outline.svg"
                      alt="Maximize icon"
                      className="min-w-6 min-h-6 inline-block align-middle ml-2"
                      width={6}
                      height={6}
                      style={{ verticalAlign: 'middle' }}
                    />
                  </button>
                )}
              <div className="flex gap-1 text-kp-blue-dark!">
                <p className="max-w-[163px]! font-semibold ">Help</p>
                <span className="inline-flex items-centergap-1"><PopUp className="ml-1! -medium"
                  headerText="Project Page Help"
                  message={`Click <a href="#" data-open-modal="project-form-example"> here </a>for an example of a completed form <br/><br/>
                Click <a href="#" data-open-modal="project-help">here </a> for additional page help <br/><br/>
                Click <a target="_blank" rel="noopener noreferrer" href="https://www.lmpartnership.org/path-to-performance/ubt-basics">here </a> for the National LMP web site`}
                /></span>
              </div>
            </div>
          </div>


      {!showManageView && (
        <DisplayProjectContainer project={project} selectedProject={selectedProject}/>
      )}
      {showManageView && (
        <div ref={manageProjectRef} tabIndex={-1}>
          <ManageProjectContainer
            key={isEditingProject ? 'manage-edit' : 'manage-create'}
            project={isEditingProject ? project : null}
            selectedProject={isEditingProject ? selectedProject : null}
            isEditing={isEditingProject}
            focusAreaList={props.focusAreaList}
            onFocusAreaChange={props.onFocusAreaChange}
            metricList={props.metricList}
            onMetricChange={props.onMetricChange}
            datasourceList={props.datasourceList}
            actionContainerRef={manageActionContainerRef}
            onClose={() => {
              const projectKey = getProjectDisplayToggleKey(project, selectedProject);
              resetProjectToggleState(projectKey);
              setGlobalToggleMode(null);
              lastKnownDisplayStateRef.current = null;
              setShowManageView(false);
              setIsEditingProject(false);
              setIsAddActionActive(false);
              setIsEditActionActive(false);
              // Reset unsaved changes when closing the form
              if (typeof props.onSetUnsavedChanges === 'function') {
                props.onSetUnsavedChanges(false);
              }
              if (!props.hasProjects) {
                setFromBannerAdd(false);
                hasTriggeredBannerAddRef.current = false;
                props.onAddFromBannerCancelled?.();
                return;
              }

              if (fromBannerAdd) {
                setFromBannerAdd(false);
                hasTriggeredBannerAddRef.current = false;
              }
            }}
            onSave={handleManageProjectSave}
            onSetUnsavedChanges={props.onSetUnsavedChanges}
          />
        </div>
      )}
      <ProjectHelpModal
        isOpen={isHelpModalOpen}
        onClose={() => setIsHelpModalOpen(false)}
      />
      <ProjectFormExampleModal
        isOpen={isFormExampleModalOpen}
        onClose={() => setIsFormExampleModalOpen(false)}
      />
    </div>
  );
};
